import DialogProvider from "@components/dialog_provider"
import { getAiProductImageIndexesQuery } from "@domain/scripts"
import useTranslate from "@hooks/use_translate"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { fmtUsd, getTryRate } from "@utils/currency"
import dayjs from "dayjs"
import type {
  AiProductImageIndexImageCounts,
  AiProductImageIndexTenant
} from "__generated__/graphql"
import { AiProductImageIndexState } from "__generated__/graphql"

const STATE_CONFIG: Record<
  AiProductImageIndexState,
  { labelKey: string; color: "success" | "warning" | "info" | "default" }
> = {
  [AiProductImageIndexState.Preparing]: {
    labelKey: "image_index_state_preparing",
    color: "info"
  },
  [AiProductImageIndexState.Running]: {
    labelKey: "image_index_state_running",
    color: "warning"
  },
  [AiProductImageIndexState.Completed]: {
    labelKey: "image_index_state_completed",
    color: "success"
  },
  [AiProductImageIndexState.Disabled]: {
    labelKey: "image_index_state_disabled",
    color: "default"
  }
}

const isRunning = (state?: AiProductImageIndexState | null) =>
  state === AiProductImageIndexState.Preparing ||
  state === AiProductImageIndexState.Running

const fmt = (value?: number | null) => (value ?? 0).toLocaleString("tr-TR")

const fmtDate = (value?: string | null) =>
  value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—"

type Props = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

function StatCard(props: { label: string; value: string; sub?: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}
    >
      <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
        {props.label}
      </Typography>
      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
        {props.value}
      </Typography>
      {props.sub ? (
        <Typography variant="caption" color="text.secondary">
          {props.sub}
        </Typography>
      ) : null}
    </Paper>
  )
}

function ImageProgress(props: { images: AiProductImageIndexImageCounts }) {
  const { total, indexed, failed } = props.images
  const percent = total > 0 ? ((indexed + failed) / total) * 100 : 0
  return (
    <Stack gap={0.5} sx={{ minWidth: "12rem" }}>
      <LinearProgress value={percent} variant="determinate" />
      <Typography variant="caption" color="text.secondary">
        {fmt(indexed + failed)} / {fmt(total)} ({percent.toFixed(2)}%)
      </Typography>
    </Stack>
  )
}

function TenantDetails(props: {
  tenant: AiProductImageIndexTenant
  tryRate: number
}) {
  const { translate } = useTranslate()
  const index = props.tenant.index
  if (!index) {
    return (
      <Alert severity="error">
        {translate("image_index_tenant_unreadable")}: {props.tenant.error}
      </Alert>
    )
  }
  const { images, listings, products, lastIndexedAt } = index.counts
  const { total, last30Days } = index.usage
  return (
    <Stack gap={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
          gap: 1
        }}
      >
        <StatCard
          label={translate("image_index_images_total")}
          value={fmt(images.total)}
        />
        <StatCard
          label={translate("image_index_images_indexed")}
          value={fmt(images.indexed)}
        />
        <StatCard
          label={translate("image_index_images_pending")}
          value={fmt(images.pending)}
        />
        <StatCard
          label={translate("image_index_images_failed")}
          value={fmt(images.failed)}
        />
        <StatCard
          label={translate("image_index_listings")}
          value={fmt(listings)}
        />
        <StatCard
          label={translate("image_index_products")}
          value={fmt(products)}
        />
        <StatCard
          label={translate("image_index_cost_total")}
          value={fmtUsd(total.estimatedCostUsd, 4)}
          sub={`≈ ₺${(total.estimatedCostUsd * props.tryRate).toFixed(2)} · ${fmt(total.indexedImages)} + ${fmt(total.queryImages)}`}
        />
        <StatCard
          label={translate("image_index_cost_30_days")}
          value={fmtUsd(last30Days.estimatedCostUsd, 4)}
          sub={`${fmt(last30Days.indexedImages)} + ${fmt(last30Days.queryImages)}`}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {translate("image_index_cost_note")}
      </Typography>

      <Stack gap={1}>
        <Typography variant="subtitle2">
          {translate("image_index_shops")}
        </Typography>
        {index.shops.map((shop) => (
          <Paper
            key={shop.shopId}
            sx={{
              p: 1.5,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap"
            }}
            variant="outlined"
          >
            <Typography fontWeight={600} sx={{ minWidth: "8rem" }}>
              {shop.type}
            </Typography>
            <Chip
              color={
                shop.connectionStatus === "DISCONNECTED" ? "default" : "success"
              }
              label={shop.connectionStatus}
              size="small"
            />
            <ImageProgress images={shop.images} />
            <Typography variant="body2" color="text.secondary">
              {translate("image_index_listings")}: {fmt(shop.listings)} ·{" "}
              {translate("image_index_images_failed")}:{" "}
              {fmt(shop.images.failed)}
            </Typography>
            {shop.connectionStatus === "DISCONNECTED" ? (
              <Typography variant="caption" color="text.secondary">
                {translate("image_index_shop_skipped")}
              </Typography>
            ) : null}
          </Paper>
        ))}
      </Stack>

      {index.errors.length ? (
        <Stack gap={1}>
          <Typography variant="subtitle2">
            {translate("image_index_errors")}
          </Typography>
          {index.errors.map((error) => (
            <Typography key={error.reason ?? "unknown"} variant="body2">
              {fmt(error.images)} × {error.reason ?? "—"}
            </Typography>
          ))}
        </Stack>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        {translate("image_index_planned_at")}: {fmtDate(index.plannedAt)} ·{" "}
        {translate("image_index_last_indexed_at")}: {fmtDate(lastIndexedAt)}
      </Typography>
    </Stack>
  )
}

/**
 * The admin side of "use product images": every tenant that switched it on, how far its index got
 * and what the embeddings cost. Read-only — tenants start it, and it cannot be cancelled.
 */
export default function ProductImageIndexStatusDialog({
  isOpen,
  setIsOpen
}: Props) {
  const { translate } = useTranslate()

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["admin-product-image-indexes"],
    queryFn: async () => (await getAiProductImageIndexesQuery()).data ?? [],
    enabled: isOpen,
    refetchInterval: (latest) =>
      (latest.state.data ?? []).some((tenant) => isRunning(tenant.index?.state))
        ? 10_000
        : false
  })

  const { data: tryRate = 0 } = useQuery({
    queryKey: ["try-rate"],
    queryFn: getTryRate,
    enabled: isOpen
  })

  const tenants = data ?? []
  const totals = tenants.reduce(
    (sum, tenant) => ({
      images: sum.images + (tenant.index?.counts.images.total ?? 0),
      done:
        sum.done +
        (tenant.index?.counts.images.indexed ?? 0) +
        (tenant.index?.counts.images.failed ?? 0),
      cost: sum.cost + (tenant.index?.usage.total.estimatedCostUsd ?? 0),
      running: sum.running + (isRunning(tenant.index?.state) ? 1 : 0)
    }),
    { images: 0, done: 0, cost: 0, running: 0 }
  )

  return (
    <DialogProvider
      title={translate("image_index_admin_title")}
      leftButton={{
        text: translate("close"),
        color: "secondary",
        onClick: () => setIsOpen(false)
      }}
      rightButton={{
        text: translate("image_index_refresh"),
        color: "primary",
        onClick: () => void refetch(),
        loading: isFetching,
        disabled: isFetching
      }}
      isOpen={isOpen}
      setClose={() => setIsOpen(false)}
      size="large"
    >
      <Stack gap={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
            gap: 1
          }}
        >
          <StatCard
            label={translate("image_index_tenants")}
            value={fmt(tenants.length)}
          />
          <StatCard
            label={translate("image_index_running_tenants")}
            value={fmt(totals.running)}
          />
          <StatCard
            label={translate("image_index_images_total")}
            value={fmt(totals.images)}
            sub={`${fmt(totals.done)} ${translate("image_index_images_done")}`}
          />
          <StatCard
            label={translate("image_index_cost_total")}
            value={fmtUsd(totals.cost, 4)}
            sub={`≈ ₺${(totals.cost * tryRate).toFixed(2)}`}
          />
        </Box>

        {isFetching && !tenants.length ? (
          <Stack alignItems="center" p={3}>
            <CircularProgress size={24} />
          </Stack>
        ) : null}
        {!isFetching && !tenants.length ? (
          <Alert severity="info">{translate("image_index_no_tenants")}</Alert>
        ) : null}

        {tenants.map((tenant) => {
          const state = tenant.index?.state
          const config = state ? STATE_CONFIG[state] : undefined
          return (
            <Accordion
              key={tenant.customerId}
              disableGutters
              variant="outlined"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={2}
                  sx={{ width: "100%", flexWrap: "wrap" }}
                >
                  <Stack sx={{ minWidth: "14rem" }}>
                    <Typography fontWeight={600}>
                      {tenant.customerName ?? tenant.customerId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tenant.customerId}
                    </Typography>
                  </Stack>
                  {config ? (
                    <Chip
                      color={config.color}
                      label={translate(config.labelKey)}
                      size="small"
                    />
                  ) : (
                    <Chip
                      color="error"
                      label={translate("image_index_state_error")}
                      size="small"
                    />
                  )}
                  {tenant.index ? (
                    <ImageProgress images={tenant.index.counts.images} />
                  ) : null}
                  {tenant.index ? (
                    <Typography variant="body2" color="text.secondary">
                      {fmtUsd(tenant.index.usage.total.estimatedCostUsd, 4)}
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TenantDetails tenant={tenant} tryRate={tryRate} />
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Stack>
    </DialogProvider>
  )
}
